import moment from 'moment';
import commonConstants from 'utils/constants';
import {
  SEARCH_IN_FOLDERS_TEXT,
  SEARCH_IN_FILMS_TEXT,
  SEARCH_IN_FOLDERS_API_TEXT,
  SEARCH_IN_FILMS_API_TEXT,
} from './constants';

export const getTextAfterSearchInLibrarySectionClick = (
  searchInLibrarySectionText: string,
  currentSearchFilmsInputValue: string,
): string => {
  let currentSearchFilmsInputValueWithoutPrefix = currentSearchFilmsInputValue;

  if (
    currentSearchFilmsInputValue.includes(SEARCH_IN_FOLDERS_TEXT) ||
    currentSearchFilmsInputValue.includes(SEARCH_IN_FILMS_TEXT)
  ) {
    const replacePattern = `(${SEARCH_IN_FOLDERS_TEXT} )|(${SEARCH_IN_FILMS_TEXT} )`;
    const regExp = new RegExp(replacePattern, 'g');

    currentSearchFilmsInputValueWithoutPrefix = currentSearchFilmsInputValue.replace(
      regExp,
      '',
    );
  }

  return `${searchInLibrarySectionText} ${currentSearchFilmsInputValueWithoutPrefix}`;
};

export const getLibrarySearchType = (
  currentSearchFilmsInputValue: string,
): string | undefined => {
  if (currentSearchFilmsInputValue.includes(SEARCH_IN_FOLDERS_TEXT))
    return SEARCH_IN_FOLDERS_API_TEXT;

  if (currentSearchFilmsInputValue.includes(SEARCH_IN_FILMS_TEXT))
    return SEARCH_IN_FILMS_API_TEXT;

  return undefined;
};

export const getLibrarySearchValue = (currentSearchFilmsInputValue: string): string => {
  const replacePattern = `(${SEARCH_IN_FOLDERS_TEXT} )|(${SEARCH_IN_FILMS_TEXT} )`;
  const regExp = new RegExp(replacePattern, 'g');

  return currentSearchFilmsInputValue.replace(regExp, '');
};

export const getNullableNumberFromString = (value: string): number | null => {
  const parsed = parseFloat(value);

  if (Number.isNaN(parsed)) return null;

  return parsed;
};

export const getNumberInUSALocaleOrDefaultString = (value: string): string => {
  let formattedValue = value;

  if (value && value.length && value.slice(-1) !== '.') {
    const valueNumber = getNullableNumberFromString(value.replace(/,/g, ''));

    if (valueNumber) {
      formattedValue = new Intl.NumberFormat('en-US').format(valueNumber);
    }
  }

  return formattedValue;
};

export const getNullableDateFromString = (
  value: string,
  dateFormat: string,
): Date | null => {
  if (moment(value, dateFormat, true).isValid()) return new Date(value);

  return null;
};

export const getEncodedUriComponentFromObject = (object: any): string =>
  encodeURIComponent(JSON.stringify(object));

export const getObjectFromEncodedUriComponent = (uriComponent: string): any =>
  JSON.parse(decodeURIComponent(uriComponent));

export const getMoreLessApiOptionValue = (option: string): string => {
  if (option === commonConstants.moreLessOptions[0]) return 'mt';

  if (option === commonConstants.moreLessOptions[1]) return 'lt';

  return 'mt';
};

interface AddRemoveInput {
  inputValue: string;
}

export const getAddRemoveInputsValue = (
  addRemoveInputs: AddRemoveInput[],
): string[] =>
  addRemoveInputs
    .filter((addRemoveInput) => addRemoveInput.inputValue.length)
    .map((addRemoveInput) => addRemoveInput.inputValue);

export const groupBy = <T extends Record<string, any>>(
  items: T[],
  key: keyof T,
): Record<string, T[]> =>
  items.reduce(
    (result, item) => ({
      ...result,
      [item[key]]: [...(result[item[key]] || []), item],
    }),
    {} as Record<string, T[]>,
  );

interface AdditionalCriteria {
  selectedInputTypeOption: string;
  inputValue: string;
}

interface AdditionalCriteriaValue {
  type: string | number;
  value: string[];
}

export const getAdditionalCriteriaInputsValue = (
  additionalCriterias: AdditionalCriteria[],
): AdditionalCriteriaValue[] => {
  const groupedCriterias = groupBy(
    additionalCriterias,
    'selectedInputTypeOption',
  );

  return commonConstants.inputTypeOptions.map((inputOption: string) => ({
    type: commonConstants.inputTypeOptionsEnum[inputOption] as string | number,
    value: groupedCriterias[inputOption]
      ? groupedCriterias[inputOption]
          .filter((criteria) => criteria.inputValue.length)
          .map((criteria) => criteria.inputValue)
      : [],
  }));
};

export const removeBlankAttributes = (obj: any): any =>
  Object.entries(obj)
    .map(([k, v]) => [
      k,
      v && typeof v === 'object' ? removeBlankAttributes(v) : v,
    ])
    // eslint-disable-next-line no-return-assign
    .reduce((a, [k, v]) => (v ? ((a[k] = v), a) : a), {} as any);

export const convertNumberIntoAbbreviatedString = (value: number): string =>
  Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: 0,
  }).format(value);

interface Suggestion {
  id: string | number;
}

export const getSelectedSuggestionsIds = (
  suggestions?: Suggestion[],
): (string | number)[] => suggestions?.map((suggestion) => suggestion.id) ?? [];
